import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { useScaffold } from "@Light/scaffold";
import type { AddressSearchResponse } from "@Light/services/lightTypes";
import clsx from "clsx";
import { useState } from "react";
import { EnrollBottomSheet } from "../common/EnrollBottomSheet";
import { EnrollHelpButton } from "../EnrollHelpButtonFooter";
import { AddressSearchField } from "./AddressSearchField";
import { type UseAddressSearch } from "./useAddressSearch";

export type AddressSearchBottomSheetProps = UseAddressSearch & {
  open: boolean;
  onClose: () => void;
  onSelect: (result: AddressSearchResponse) => void;
  onNotFound: () => void;
  onClickUtilityAccountNumber?: () => void;
  /** Force address list open (only used by stories) */
  forceOpen?: boolean;
};

export const AddressSearchBottomSheet = ({
  open,
  onClose,
  onSelect,
  onNotFound,
  onClickUtilityAccountNumber,
  forceOpen,
  ...search
}: AddressSearchBottomSheetProps) => {
  const scaffold = useScaffold();
  const [isFocused, setIsFocused] = useState(false);

  return (
    <EnrollBottomSheet
      open={open}
      onClose={onClose}
      title="What’s your address?"
      subtitle="This will help us find out your eligibility and accurate pricing."
      data-testid="address-search-sheet"
      footer={
        <ButtonFooter>
          <EnrollHelpButton eventVariant="address_sheet" />
        </ButtonFooter>
      }
    >
      <div className="flex flex-col gap-1.5">
        <AddressSearchField
          {...search}
          onSelect={onSelect}
          onNotFound={onNotFound}
          forceOpen={forceOpen}
          placeholder="123 Main St."
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
        />
        {onClickUtilityAccountNumber ? (
          <p className={clsx("text-sm", scaffold.page.colors.text.tertiary)}>
            Know your ESI ID?{" "}
            <button
              type="button"
              onClick={onClickUtilityAccountNumber}
              data-testid="address-search-sheet-use-utility-account-number"
              className="cursor-pointer underline underline-offset-4"
            >
              Use ESI ID
            </button>
          </p>
        ) : null}
        <div
          aria-hidden
          className={clsx(
            "shrink-0 transition-[height] duration-200 ease-out",
            isFocused || forceOpen ? "h-48" : "h-0",
          )}
        />
      </div>
    </EnrollBottomSheet>
  );
};
